import { createContext, useContext, useEffect, useState } from "react";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { useChatChannelRecipients } from "./utils/use-chat-channel-recipients";

import type { DialogController } from "../../../utils/hooks/use-dialog";
import type { UUID } from "../../../utils/uuid";
import type { ChannelName } from "../../channels/types";
import type { TagGroup } from "../../merge-tag/components/types";
import type { EditorLinkData } from "./plugins/toolbar/hooks/use-link-button-on-click";
import type { SaveDHForm } from "./plugins/toolbar/save-dh-modal-plugin";
import type { SaveImageForm } from "./plugins/toolbar/save-image-modal-plugin";
import type { NodeKey } from "lexical";
import type { FC, ReactNode } from "react";

export type EditorModalState =
  | {
      type: "link";
      data: EditorLinkData;
    }
  | {
      type: "image";
      data: SaveImageForm & { mimeType: string };
    }
  | { type: "design-huddle"; data: SaveDHForm }
  | {
      type: "dynamic-content";
      data:
        | {
            mode: "add";
            segmentId?: undefined;
          }
        | {
            mode: "edit";
            key: NodeKey;
            segmentId: UUID;
          };
    }
  | {
      type: "poll";
      data: { mode: "add" } | { mode: "edit"; key: NodeKey };
    };

interface LexicalContextType {
  format: "email" | "chat" | "sms";
  toChannel: boolean;
  disabled: boolean;
  channelName: ChannelName;
  filterTags: (x: string) => TagGroup;
  isSurveyTrigger: boolean;
  triggerSurveyId: string | null;
  triggerSurveyName: string | null;
  focus: {
    focused: boolean;
    setFocused: (focused: boolean) => void;
  };
  drag: {
    draggedOver: boolean;
    setDraggedOver: (draggedOver: boolean) => void;
  };
  upload: {
    uploading: boolean;
    setUploading: (uploading: boolean) => void;
  };
  editorModal: DialogController<EditorModalState>;
  floatingAnchor: {
    floatingAnchorElem: HTMLDivElement | null;
    setFloatingAnchorElem: (elem: HTMLDivElement | null) => void;
  };
  chatChannelRecipients: ReturnType<typeof useChatChannelRecipients>;
  text: {
    characterCount: number | null;
    setCharacterCount: (characterCount: number) => void;
  };
}

const LexicalContext = createContext<LexicalContextType | null>(null);

export const LexicalContextProvider: FC<{
  disabled: boolean;
  filterTags: (x: string) => TagGroup;
  children: ReactNode;
  format: "email" | "chat" | "sms";
  channelName: ChannelName;
  toChannel?: boolean;
  isSurveyTrigger?: boolean;
  triggerSurveyId?: string | null;
  triggerSurveyName?: string | null;
}> = ({
  disabled,
  filterTags,
  format,
  children,
  channelName,
  toChannel = false,
  isSurveyTrigger = false,
  triggerSurveyId = null,
  triggerSurveyName = null,
}) => {
  const [focused, setFocused] = useState(false);
  const [draggedOver, setDraggedOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorModal = useDialog<EditorModalState>();
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [characterCount, setCharacterCount] = useState<number | null>(null);

  const chatChannelRecipients = useChatChannelRecipients(channelName);

  useEffect(() => {
    chatChannelRecipients.reload();
    // Run this at first load to populate it.
  }, []);

  return (
    <LexicalContext.Provider
      value={{
        format,
        disabled,
        filterTags,
        chatChannelRecipients,
        editorModal,
        toChannel,
        channelName,
        isSurveyTrigger,
        triggerSurveyId,
        triggerSurveyName,
        focus: { focused, setFocused },
        drag: { draggedOver, setDraggedOver },
        upload: { uploading, setUploading },
        floatingAnchor: {
          floatingAnchorElem,
          setFloatingAnchorElem,
        },
        text: { characterCount, setCharacterCount },
      }}
    >
      {children}
    </LexicalContext.Provider>
  );
};

export const useLexicalContext = (): LexicalContextType => {
  const context = useContext(LexicalContext);
  if (!context) {
    throw new Error("useLexicalContext must be used within a LexicalContextProvider");
  }
  return context;
};
